import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, Check, X, AlertCircle } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { useLanguage } from '../i18n/LanguageContext';
import { getOffer, pick, withSubId, type Lang } from '../offers/config';

// Textos fijos de la plantilla de oferta, en los dos idiomas.
const UI = {
  es: {
    notFound: 'Esta pagina no existe',
    backHome: 'Volver al inicio',
    updated: 'Actualizado',
    readTime: (n: number) => `Lectura de ${n} min`,
    confidence: 'Nivel de Confianza',
    disclosureLabel: 'Divulgacion:',
    disclosure: 'Blinkblis recibe una comision si abres una cuenta a traves de los enlaces de esta pagina. No te cuesta nada adicional y no cambia lo que recomendamos.',
    disclosureLink: 'Como trabajamos',
    problemTitle: 'El problema real',
    problem: ['Si llegaste a Estados Unidos hace poco, o simplemente nunca pediste credito, estas en una situacion que el sistema no resuelve bien: para que te den credito necesitas historial, y para tener historial necesitas credito.', 'No es un problema de ingresos. Puedes ganar bien, pagar todo a tiempo y en efectivo, y aun asi aparecer como invisible ante los buros.', 'En Estados Unidos pagar bien no construye historial. Solo construye historial aquello que se reporta.', 'Esa es la unica puerta: necesitas una cuenta que reporte.'],
    whatTitle: 'Que hace realmente un credit builder',
    what: 'Un producto de credit builder existe para una sola cosa: crear actividad que se reporte a los buros de credito, sin exigirte historial previo para abrirla. Abres una cuenta pequena, haces pagos mensuales, y el proveedor los reporta. Lo que se construye ahi es historial de pagos, el factor de mayor peso en la mayoria de los modelos de puntaje.',
    notDoTitle: 'Lo que NO hace',
    notDo: ['No borra ni corrige informacion negativa existente', 'No garantiza aprobacion en ningun producto futuro', 'No produce resultados inmediatos', 'No sustituye pagar tus otras obligaciones a tiempo'],
    notDoNote: 'Cualquiera que te prometa un numero de puntos en un plazo determinado te esta vendiendo algo que no puede entregar. Los resultados dependen de tu situacion particular y nadie puede garantizarlos.',
    criteriaTitle: 'Como lo evaluamos',
    criteria: [{
      t: 'Costo real mensual',
      d: 'Cuanto pagas y que recibes exactamente. Un producto barato que no reporta no sirve de nada.'
    }, {
      t: 'Requisitos de entrada',
      d: 'Pide historial previo? Revision dura? Deposito grande? Menos barreras, mas util para quien empieza.'
    }, {
      t: 'A que buros reporta',
      d: 'Reportar a un solo buro construye un tercio del historial que construye reportar a los tres.'
    }, {
      t: 'Transparencia y salida',
      d: 'Puedes cancelar sin penalizacion? Los terminos estan claros antes de dar tus datos?'
    }],
    recTitle: 'Nuestra recomendacion',
    confidenceBadge: 'Confianza',
    pros: 'A favor',
    cons: 'En contra',
    affiliateNote: (network: string) => `Enlace de afiliado via ${network}. Blinkblis recibe una comision si abres una cuenta. Tu costo es el mismo.`,
    notForTitle: 'Para quien NO es esto',
    notForIntro: 'Te lo decimos claro porque preferimos que no abras una cuenta que no necesitas.',
    faqTitle: 'Preguntas frecuentes',
    finalTitle: 'Listo para empezar?',
    finalText: 'Toma unos minutos desde el telefono. Si despues de leer esto crees que no es para ti, no lo abras: tambien es una decision valida.',
    footerNote: 'Este contenido es informativo y no constituye asesoria financiera. Los terminos de los productos cambian; verifica siempre directamente con el proveedor. Ver',
    methodology: 'Metodologia',
    disclosurePage: 'Divulgacion de Afiliados',
    and: 'y'
  },
  en: {
    notFound: 'This page does not exist',
    backHome: 'Back to home',
    updated: 'Updated',
    readTime: (n: number) => `${n} min read`,
    confidence: 'Confidence Level',
    disclosureLabel: 'Disclosure:',
    disclosure: 'Blinkblis earns a commission if you open an account through the links on this page. It costs you nothing extra and it does not change what we recommend.',
    disclosureLink: 'How we work',
    problemTitle: 'The real problem',
    problem: ['If you recently arrived in the United States, or you simply never borrowed, you are in a situation the system handles badly: to get credit you need history, and to have history you need credit.', 'It is not an income problem. You can earn well, pay everything on time and in cash, and still be invisible to the bureaus.', 'In the United States, paying well does not build history. Only what gets reported builds history.', 'That is the only door: you need an account that reports.'],
    whatTitle: 'What a credit builder actually does',
    what: 'A credit builder product exists for one thing: to create activity that gets reported to the credit bureaus, without requiring prior history to open it. You open a small account, you make monthly payments, and the provider reports them. What gets built there is payment history, the heaviest factor in most scoring models.',
    notDoTitle: 'What it does NOT do',
    notDo: ['It does not erase or correct existing negative information', 'It does not guarantee approval for any future product', 'It does not produce immediate results', 'It does not replace paying your other obligations on time'],
    notDoNote: 'Anyone promising you a specific number of points in a specific timeframe is selling something they cannot deliver. Results depend on your particular situation and nobody can guarantee them.',
    criteriaTitle: 'How we evaluate it',
    criteria: [{
      t: 'Real monthly cost',
      d: 'What you pay and what exactly you get. A cheap product that does not report is worth nothing.'
    }, {
      t: 'Entry requirements',
      d: 'Does it require prior history? A hard pull? A large deposit? Fewer barriers, more useful for someone starting out.'
    }, {
      t: 'Which bureaus it reports to',
      d: 'Reporting to one bureau builds a third of the history that reporting to all three does.'
    }, {
      t: 'Transparency and exit',
      d: 'Can you cancel without penalty? Are the terms clear before you hand over your data?'
    }],
    recTitle: 'Our recommendation',
    confidenceBadge: 'Confidence',
    pros: 'Pros',
    cons: 'Cons',
    affiliateNote: (network: string) => `Affiliate link via ${network}. Blinkblis earns a commission if you open an account. Your cost is the same.`,
    notForTitle: 'Who this is NOT for',
    notForIntro: 'We say it plainly because we would rather you not open an account you do not need.',
    faqTitle: 'Frequently asked questions',
    finalTitle: 'Ready to start?',
    finalText: 'It takes a few minutes from your phone. If after reading this you think it is not for you, do not open it: that is a valid decision too.',
    footerNote: 'This content is informational and does not constitute financial advice. Product terms change; always verify directly with the provider. See',
    methodology: 'Methodology',
    disclosurePage: 'Affiliate Disclosure',
    and: 'and'
  }
};
export function Offer() {
  const {
    slug
  } = useParams();
  const {
    language
  } = useLanguage();
  const lang = (language === 'en' ? 'en' : 'es') as Lang;
  const t = UI[lang];
  const offer = getOffer(slug);
  const legalPath = (key: 'metodologia' | 'divulgacion-de-afiliados') => `/${key}`;
  if (!offer) {
    return <div className="min-h-screen bg-background text-white flex flex-col" data-id="element-399" data-mp="1c1hvpypts824">
        <Navbar data-id="element-400" data-mp="1bzf79j4bmmzi" />
        <div className="flex-1 flex flex-col items-center justify-center gap-6 px-6 text-center" data-id="element-401" data-mp="1xcgexw0bf0id">
          <h1 className="font-display text-3xl" data-id="element-402" data-mp="1tav6qnyq7aei">{t.notFound}</h1>
          <Link to="/" className="text-accent hover:underline" data-id="element-403" data-mp="1tabpzaktas22">
            {t.backHome}
          </Link>
        </div>
        <Footer data-id="element-404" data-mp="278jo3ltl3zet" />
      </div>;
  }
  const href = withSubId(offer.affiliateUrl, offer.slug);
  const facts = pick(offer.facts, lang);
  const pros = pick(offer.pros, lang);
  const cons = pick(offer.cons, lang);
  const notFor = pick(offer.notFor, lang);
  const faq = pick(offer.faq, lang);
  const fireLead = () => {
    const w = window as any;
    if (typeof w.fbq === 'function') {
      w.fbq('track', 'Lead', {
        content_name: offer.brand,
        content_category: pick(offer.category, lang)
      });
    }
  };
  const Cta = ({
    full = false
  }: {
    full?: boolean;
  }) => <a href={href} target="_blank" rel="nofollow sponsored noopener noreferrer" onClick={fireLead} className={`${full ? 'w-full' : 'w-full sm:w-auto'} inline-flex items-center justify-center gap-2 bg-accent text-background px-7 h-[56px] rounded-full text-[16px] font-semibold hover:brightness-110 active:scale-[0.98] transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60`} data-id="element-405" data-mp="vtmojhzrxrt7">
      {pick(offer.ctaLabel, lang)}
      <ArrowRight className="w-[18px] h-[18px]" data-id="element-406" data-mp="2xlogvum85egu" />
    </a>;
  return <div className="min-h-screen bg-background text-white" data-id="element-407" data-mp="3l9plisga8aqz">
      <Navbar data-id="element-408" data-mp="3l84pszi8elrj" />

      <article className="max-w-[760px] mx-auto px-5 sm:px-6 pt-32 md:pt-40 pb-24" data-id="element-409" data-mp="3q9bxyzoadmyq">
        <div className="flex items-center gap-2 text-[13px] text-white/40 mb-8" data-id="element-410" data-mp="3q77u2hqe0nyg">
          <Link to="/" className="hover:text-white/70 transition-colors" data-id="element-411" data-mp="3c99jzoj3ndor">
            Blinkblis
          </Link>
          <span data-id="element-412" data-mp="3haqi5ca78m1n">/</span>
          <span className="text-accent" data-id="element-413" data-mp="20udybim1cbyp">{pick(offer.category, lang)}</span>
        </div>

        <h1 className="font-display text-[34px] sm:text-5xl font-semibold tracking-[-0.03em] leading-[1.08] mb-6" data-id="element-414" data-mp="20wqj03j6h0fg">
          {pick(offer.title, lang)}
        </h1>

        <p className="text-[18px] sm:text-xl text-white/55 font-light leading-relaxed mb-8" data-id="element-415" data-mp="21ciwlhsbzcdn">
          {pick(offer.subtitle, lang)}
        </p>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[13px] text-white/40 mb-8 pb-8 border-b border-white/10" data-id="element-416" data-mp="1rmfums38eo36">
          <span data-id="element-417" data-mp="1wk917m8g5k3z">
            {t.updated}: {pick(offer.updated, lang)}
          </span>
          <span className="text-white/20" data-id="element-418" data-mp="1hnqmvs51uk0n">·</span>
          <span data-id="element-419" data-mp="1hq4gdwk2zo63">{t.readTime(offer.readMinutes)}</span>
          <span className="text-white/20" data-id="element-420" data-mp="1mncxruxeno6a">·</span>
          <Link to={legalPath('metodologia')} className="text-accent hover:underline" data-id="element-421" data-mp="1ml0bx6oj0tny">
            {t.confidence} {offer.confidence}%
          </Link>
        </div>

        <div className="mb-14 border-l-2 border-accent bg-white/[0.03] rounded-r-xl px-5 py-4 text-[15px] leading-relaxed text-white/60" data-id="element-422" data-mp="1do9h0xlpnkuy">
          <span className="text-white/85 font-medium" data-id="element-423" data-mp="380pjrofasgv2">
            {t.disclosureLabel}
          </span>{' '}
          {t.disclosure}{' '}
          <Link to={legalPath('divulgacion-de-afiliados')} className="text-accent hover:underline whitespace-nowrap" data-id="element-424" data-mp="3i4mfcufnmxho">
            {t.disclosureLink} →
          </Link>
        </div>

        <section className="mb-14" data-id="element-425" data-mp="3qysnzmunnyy1">
          <h2 className="font-display text-[26px] sm:text-3xl font-semibold tracking-[-0.02em] mb-5" data-id="element-426" data-mp="3m5g2hll9w89i">
            {t.problemTitle}
          </h2>
          <div className="space-y-5 text-[17px] leading-[1.75] text-white/65 font-light" data-id="element-427" data-mp="4nn5cfju9uq1">
            <p data-id="element-428" data-mp="3vv9dakdlcjcp">{t.problem[0]}</p>
            <p data-id="element-429" data-mp="3vy5fikwh1u6y">
              {t.problem[1]}{' '}
              <span className="text-white/90" data-id="element-430" data-mp="dov2guamcpij">{t.problem[2]}</span>
            </p>
            <p data-id="element-431" data-mp="9ob79w9lvvz1">{t.problem[3]}</p>
          </div>
        </section>

        <section className="mb-14" data-id="element-432" data-mp="2fhj8fbendr20">
          <h2 className="font-display text-[26px] sm:text-3xl font-semibold tracking-[-0.02em] mb-5" data-id="element-433" data-mp="2fgrfk8cfq7ff">
            {t.whatTitle}
          </h2>
          <p className="text-[17px] leading-[1.75] text-white/65 font-light" data-id="element-434" data-mp="2bi0pgl6dzq1t">
            {t.what}
          </p>

          <div className="mt-7 rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-id="element-435" data-mp="eho0j7btmd61">
            <div className="flex items-center gap-2 mb-4" data-id="element-436" data-mp="jgh9cmg5xfzv">
              <AlertCircle className="w-[18px] h-[18px] text-accent" data-id="element-437" data-mp="ocg4csvzxw99" />
              <h3 className="text-white font-medium text-[15px]" data-id="element-438" data-mp="td3pixxjnivk">
                {t.notDoTitle}
              </h3>
            </div>
            <ul className="space-y-3" data-id="element-439" data-mp="xde8db6fq3ru">
              {t.notDo.map(item => <li key={item} className="flex gap-3 text-[15px] text-white/55 leading-relaxed" data-id="element-440" data-mp="12ch0xw58ahix">
                  <X className="w-4 h-4 text-white/30 shrink-0 mt-1" data-id="element-441" data-mp="176203rik5hjp" />
                  {item}
                </li>)}
            </ul>
            <p className="mt-5 pt-5 border-t border-white/10 text-[15px] text-white/55 leading-relaxed" data-id="element-442" data-mp="1h2y4pm5c5jiz">
              {t.notDoNote}
            </p>
          </div>
        </section>

        <section className="mb-14" data-id="element-443" data-mp="35mal5q16tbk0">
          <h2 className="font-display text-[26px] sm:text-3xl font-semibold tracking-[-0.02em] mb-6" data-id="element-444" data-mp="35mkglt504ax3">
            {t.criteriaTitle}
          </h2>
          <div className="grid sm:grid-cols-2 gap-4" data-id="element-445" data-mp="2wl322f78shai">
            {t.criteria.map(c => <div key={c.t} className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-id="element-446" data-mp="2rlzuamzvnwyu">
                <h3 className="text-white font-medium mb-2 text-[15px]" data-id="element-447" data-mp="3p8qjjld7ursx">
                  {c.t}
                </h3>
                <p className="text-[14px] text-white/50 leading-relaxed font-light" data-id="element-448" data-mp="3kb72yet1q2xt">
                  {c.d}
                </p>
              </div>)}
          </div>
        </section>

        <section className="mb-14" data-id="element-449" data-mp="3akcfx87z6zbb">
          <h2 className="font-display text-[26px] sm:text-3xl font-semibold tracking-[-0.02em] mb-6" data-id="element-450" data-mp="buvgucq5x0w6">
            {t.recTitle}
          </h2>

          <div className="rounded-3xl border border-accent/25 bg-accent/[0.04] p-6 sm:p-8" data-id="element-451" data-mp="733zmsswc7ip">
            <div className="flex flex-wrap items-center gap-3 mb-6" data-id="element-452" data-mp="3nr9wt1vu5hxe">
              <span className="font-display text-3xl font-semibold" data-id="element-453" data-mp="3rsv8ijwmvq60">
                {offer.brand}
              </span>
              <span className="text-[12px] font-medium text-accent border border-accent/40 rounded-full px-3 py-1" data-id="element-454" data-mp="3du5b135wjoh4">
                {t.confidenceBadge} {offer.confidence}%
              </span>
            </div>

            {facts.length > 0 && <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3 mb-7 pb-7 border-b border-white/10" data-id="element-455" data-mp="aeq4ibwqfx0q">
                {facts.map(f => <div key={f.label} className="flex items-baseline justify-between gap-4" data-id="element-456" data-mp="nbxzk6d5wy3">
                    <span className="text-[14px] text-white/45" data-id="element-457" data-mp="5k2fmbxth0lm">{f.label}</span>
                    <span className="text-[15px] text-white font-medium text-right" data-id="element-458" data-mp="tapp31ndryaa">
                      {f.value}
                    </span>
                  </div>)}
              </div>}

            <div className="grid sm:grid-cols-2 gap-8 mb-8" data-id="element-459" data-mp="2iqsb2jb26uuy">
              <div data-id="element-460" data-mp="2dubhvap05qb0">
                <h4 className="text-[13px] uppercase tracking-wider text-white/40 mb-4" data-id="element-461" data-mp="2dt0idn2lb4pp">
                  {t.pros}
                </h4>
                <ul className="space-y-3" data-id="element-462" data-mp="2rmrk49a2xkav">
                  {pros.map(p => <li key={p} className="flex gap-2.5 text-[15px] text-white/70 leading-relaxed" data-id="element-463" data-mp="2nlfzki62bp9j">
                      <Check className="w-4 h-4 text-accent shrink-0 mt-1" data-id="element-464" data-mp="1uvzb1vlkcsil" />
                      {p}
                    </li>)}
                </ul>
              </div>
              <div data-id="element-465" data-mp="23v2s6u166xug">
                <h4 className="text-[13px] uppercase tracking-wider text-white/40 mb-4" data-id="element-466" data-mp="23ukm75oph59c">
                  {t.cons}
                </h4>
                <ul className="space-y-3" data-id="element-467" data-mp="3kbf8idq4kpsn">
                  {cons.map(c => <li key={c} className="flex gap-2.5 text-[15px] text-white/70 leading-relaxed" data-id="element-468" data-mp="3fclsoend8mpb">
                      <X className="w-4 h-4 text-white/30 shrink-0 mt-1" data-id="element-469" data-mp="2hv4t9ovdt6lf" />
                      {c}
                    </li>)}
                </ul>
              </div>
            </div>

            <Cta data-id="element-470" data-mp="2qvkxx1nv2jrv" />
            <p className="mt-4 text-[13px] text-white/35 leading-relaxed" data-id="element-471" data-mp="2qw334nsi48bx">
              {t.affiliateNote(offer.network)}
            </p>
          </div>
        </section>

        <section className="mb-14" data-id="element-472" data-mp="1y98kzxj5pl1m">
          <h2 className="font-display text-[26px] sm:text-3xl font-semibold tracking-[-0.02em] mb-3" data-id="element-473" data-mp="1y6488brmlgzc">
            {t.notForTitle}
          </h2>
          <p className="text-[16px] text-white/50 font-light mb-6" data-id="element-474" data-mp="27yjsjfqeahne">
            {t.notForIntro}
          </p>
          <ul className="space-y-4" data-id="element-475" data-mp="1aje5t9bd7ke7">
            {notFor.map(n => <li key={n} className="flex gap-3 text-[16px] text-white/65 leading-relaxed font-light" data-id="element-476" data-mp="1fhfqfyofvrrs">
                <span className="text-accent shrink-0" data-id="element-477" data-mp="17xodsouuselb">—</span>
                {n}
              </li>)}
          </ul>
        </section>

        <section className="mb-14" data-id="element-478" data-mp="xzqody19cbr4">
          <h2 className="font-display text-[26px] sm:text-3xl font-semibold tracking-[-0.02em] mb-6" data-id="element-479" data-mp="xypdbtk8rp9e">
            {t.faqTitle}
          </h2>
          <div className="space-y-3" data-id="element-480" data-mp="u0rmkmatov3w">
            {faq.map(f => <details key={f.q} className="group rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden" data-id="element-481" data-mp="p0wuwgj841hs">
                <summary className="cursor-pointer list-none px-5 py-4 text-[16px] text-white/85 font-medium flex items-center justify-between gap-4 min-h-[56px]" data-id="element-482" data-mp="f8r7lk3x288x">
                  {f.q}
                  <span className="text-accent text-xl shrink-0 group-open:rotate-45 transition-transform duration-200" data-id="element-483" data-mp="69chilrnvzoy">
                    +
                  </span>
                </summary>
                <p className="px-5 pb-5 text-[15px] text-white/55 leading-relaxed font-light" data-id="element-484" data-mp="1p6pblmo5iu02">
                  {f.a}
                </p>
              </details>)}
          </div>
        </section>

        <section className="rounded-3xl border border-white/10 bg-white/[0.02] p-6 sm:p-8 text-center" data-id="element-485" data-mp="1g6ankfjsz0sv">
          <h3 className="font-display text-2xl font-semibold mb-3" data-id="element-486" data-mp="1k6l4uod1pdn1">
            {t.finalTitle}
          </h3>
          <p className="text-[15px] text-white/50 mb-7 font-light max-w-md mx-auto leading-relaxed" data-id="element-487" data-mp="16ewr6gn5eh3y">
            {t.finalText}
          </p>
          <Cta full data-id="element-488" data-mp="1b8ra3huxis1z" />
        </section>

        <p className="mt-12 text-[13px] text-white/35 leading-relaxed text-center" data-id="element-489" data-mp="wia73u2nbu2z">
          {t.footerNote}{' '}
          <Link to={legalPath('metodologia')} className="text-accent hover:underline" data-id="element-490" data-mp="11hwmoa5y5mey">
            {t.methodology}
          </Link>{' '}
          {t.and}{' '}
          <Link to={legalPath('divulgacion-de-afiliados')} className="text-accent hover:underline" data-id="element-491" data-mp="sgge87yozudd">
            {t.disclosurePage}
          </Link>
          .
        </p>
      </article>

      <div className="sm:hidden fixed bottom-0 left-0 right-0 z-30 p-4 bg-background/90 backdrop-blur-lg border-t border-white/10" data-id="element-492" data-mp="3wv15lzoq4vk">
        <a href={href} target="_blank" rel="nofollow sponsored noopener noreferrer" onClick={fireLead} className="w-full inline-flex items-center justify-center gap-2 bg-accent text-background h-[52px] rounded-full text-[16px] font-semibold active:scale-[0.98] transition-transform" data-id="element-493" data-mp="hsqgxiysezuq">
          {pick(offer.ctaLabel, lang)}
          <ArrowRight className="w-[18px] h-[18px]" data-id="element-494" data-mp="11l55w8quv6qd" />
        </a>
      </div>

      <Footer data-id="element-495" data-mp="wlai4e2dmx9j" />
    </div>;
}